import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setFlashToast } from '../../../utils/flashToast';
import * as civilAppellateService from '../../../services/civilAppellateCaseRequests';
import { getProfile, type ExtranetUserProfile } from '../../../services/userProfile';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { useCivilAppellateWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CriminalWizardDraft } from '../../../types/criminalCaseRequest';
import { useCivilAppellateLookups } from './useCivilAppellateLookups';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { CriminalReviewDocumentsTable } from '../criminal/CriminalReviewDocumentsTable';
import { apiOrLookupLabel, deliveryMethodDisplay } from '../../view-request/viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../criminal/CriminalReviewUi';
import CommonCoverNoteModal from '../../../components/wizard/CommonCoverNoteModal';
import { CivilAppellateWizardActions } from './CivilAppellateWizardActions';
import { civilAppellateWizardPath } from './CivilAppellateWizardShell';
import { INCORPORATED_BODY_TYPE_LABELS } from './civilAppellateConstants';
import { isOtherCorporateInstitute } from './civilAppellateHelpers';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function CivilAppellateReviewPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  const { draft, setDraft } = useCivilAppellateWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<ExtranetUserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const lookups = useCivilAppellateLookups('review', draft.provinceId);

  useEffect(() => {
    let mounted = true;
    void getProfile().then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft as unknown as CriminalWizardDraft, lookups.deliveryMethods),
    [draft, lookups.deliveryMethods],
  );

  const instituteLabel =
    draft.instituteType === 'corporation'
      ? isOtherCorporateInstitute(draft.corporateInstituteId, draft.corporateInstituteName)
        ? [draft.corporateInstituteOther, draft.corporateInstituteName].filter(Boolean).join(' · ') ||
          'Corporation'
        : draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';

  const judgmentFiles = draft.judgmentOrOrderDocuments
    .map((f) => f.originalFileName || f.fileName)
    .filter(Boolean)
    .join(', ');

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      { key: 'confirmCorrect', label: 'Confirmation', value: draft.confirmCorrect ? 'yes' : '', elementId: 'capl-confirm' },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await civilAppellateService.saveCivilAppellateRequest(draft, {
      isDraft: false,
      allSteps: true,
      confirmedCorrect: true,
    });
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? 'Submission failed.', variant: 'error' });
      return;
    }
    setDraft((d) => ({
      ...d,
      draftRequestId: r.data.requestId,
      referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
    }));
    setFlashToast({ message: 'Case Request submitted successfully.', variant: 'success' });
    setCoverNoteRequestId(r.data.requestId);
    setCoverNoteFileRef(r.data.fileReferenceNumber ?? draft.referenceNo ?? '');
    setShowCoverNote(true);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  const reviewCrumbTo = civilAppellateWizardPath(1, draft.draftRequestId);
  const editTo = (step: 1 | 2 | 3 | 4 | 5) => civilAppellateWizardPath(step, draft.draftRequestId);

  return (
    <div className="ccr-review-page">
      <CommonCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        showSuccessBanner
        onClose={closeCoverNote}
      />
      <div className="bc">
        <Link to={tasksListPath}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        <Link to={reviewCrumbTo}>Create Case Request</Link>
        <span className="bc-sep">/</span>
        Review Before Submission
      </div>
      <div className="pg-title">Review Case Request Details</div>

      <ReviewSection step="Step 01" title="Case Overview" editTo={editTo(1)}>
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            <ReviewField label="Institute Type" value={instituteLabel} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label="Appeal Matter" value={yesNoLabel(draft.isAppealMatter)} />
            {draft.isAppealMatter === 'yes' ? (
              <>
                <ReviewField label="Original Court" value={draft.originalCourt} />
                <ReviewField label="Judgement/Order" value={judgmentFiles || '—'} span={2} />
              </>
            ) : null}
            <ReviewField
              label="Province"
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label="District"
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={yesNoLabel(draft.hasConnectedMatters)} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                <ReviewField label="Connected AGD File Reference Number" value={draft.connectedAgdFileRef} />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Party Details" editTo={editTo(2)}>
        <ReviewGrid cols={4}>
          <ReviewField label="Plaintiff / Applicant Name" value={draft.plaintiffName} />
          <ReviewField label="Defendant / Respondent Name" value={draft.defendantName} />
          <ReviewField
            label="Select one of the following"
            value={
              draft.isIncorporatedBody
                ? INCORPORATED_BODY_TYPE_LABELS.incorporated
                : INCORPORATED_BODY_TYPE_LABELS.unincorporated
            }
            span={2}
          />
          <ReviewField label="Other Involved Institutions" value={draft.otherInvolvedInstitutions} span={2} />
          <ReviewField label="Name of the Legal Officer/ Officer In-Charge" value={draft.legalOfficerName} />
          <ReviewField label="Contact Number" value={draft.legalOfficerContact} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Timeline" editTo={editTo(3)}>
        <ReviewGrid cols={3}>
          <ReviewField
            label="Urgency Level"
            value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
          />
          <ReviewField label="Deadline" value={formatDisplayDate(draft.deadline)} />
          <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Submission & Physical File Delivery" editTo={editTo(4)}>
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField
              label="Submitted Division/ Unit"
              value={profile?.divisionUnit?.trim() || profile?.externalAgencyName?.trim() || '—'}
            />
            <ReviewField label="Designation" value={profile?.contactDesignation?.trim() || '—'} />
            <ReviewField label="Submitted by" value={profile?.displayName?.trim() || '—'} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={profile?.email?.trim() || '—'} />
            <ReviewField label="Contact Number" value={profile?.contactNumber?.trim() || '—'} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Physical File Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 05" title="Upload Documents" editTo={editTo(5)}>
        <ReviewSubBlock>
          <CriminalReviewDocumentsTable documents={draft.documents} />
        </ReviewSubBlock>
      </ReviewSection>

      <div className="confirm-section ccr-review-confirm">
        <label className="ccr-chk" id="capl-confirm">
          <input
            type="checkbox"
            checked={draft.confirmCorrect}
            disabled={submitting}
            onChange={(e) => {
              setDraft((d) => ({ ...d, confirmCorrect: e.target.checked }));
              if (e.target.checked) setErrors((er) => ({ ...er, confirmCorrect: '' }));
            }}
          />
          <span className="ccr-review-confirm-text">
            <span className="ccr-review-confirm-main">
              I confirm the above information is correct <span className="req">*</span>
            </span>
            <span className="ccr-review-confirm-hint">Please confirm before submitting this request to AGD.</span>
          </span>
        </label>
        {errors.confirmCorrect ? <div className="ccr-review-confirm-err">{errors.confirmCorrect}</div> : null}
      </div>

      <CivilAppellateWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={5}
        onBack={() => nav(editTo(5))}
        showSubmit
        submitting={submitting}
        onSubmit={() => void submit()}
        reviewMode
      />
    </div>
  );
}
